import React, { useCallback, useEffect, useMemo } from 'react';
import { IHttpClient } from '@wix/http-client';
import { ChatMessageData } from '@wix/chat-widget-ui-lib';
import {
  ChatWidgetLog,
  MessageRequest,
} from './useChatServiceProvider/domain/logs';
import { OnFunctionCall } from './useChatServiceProvider/domain/functions';
import { useLiveChat } from './useChatServiceProvider/infra/chat/useLiveChat';
import { useConsentClientTool } from './useChatServiceProvider/infra/chat/useConsentClientTool';
import { ToolExecutionRegisry } from './types';
import { ChatServiceHttpClient } from './ChatServiceHttpClient';

const waitingMessage: ChatMessageData = {
  content: '',
  role: 'assistant',
};

export interface DomainReference {
  domainId: string;
  options?: { revisionTag?: string };
}

export interface ChatServiceProviderProps {
  httpClient: IHttpClient;
  agentName?: string | null;
  domainIds: string[] | DomainReference[];
  noValidate?: boolean;
  enableLogging?: boolean;

  // @deprecated: under research, please do not use it
  onFunctionCall?: OnFunctionCall;
}

type CreateConversation = (params: {
  origin: string;
  firstUserMessage?: string;
  tools?: ToolExecutionRegisry;
  /**
   * @deprecated: renamed to firstUserMessage
   */
  firstAssistantMessageContent?: string;
}) => Promise<string>;

export interface ChatServiceProvider {
  isLoading: boolean;
  statusMessage: string;
  conversationId: string | ''; // "" means no conversation yet
  createConversation: CreateConversation;
  messages: ChatMessageData[];
  sendUserMessage: (userMessageContent: string) => Promise<void>;
  messageRequests: MessageRequest[];

  /**
   * @deprecated: use messageRequests instead
   */
  logs: ChatWidgetLog[];
}

export const useChatServiceProvider = ({
  httpClient,
  domainIds,
  noValidate,
  enableLogging,
  agentName,
}: ChatServiceProviderProps): ChatServiceProvider => {
  const chatClient = React.useMemo(
    () => new ChatServiceHttpClient(httpClient),
    [httpClient],
  );
  const [firstUserMessage, setFirstUserMessage] = React.useState<string>();
  const {
    conversationId,
    messages,
    requests,
    sendMessage,
    setChat,
    statusMessage,
    isConversationActive,
    createLoggerWithMeasure,
  } = useLiveChat(agentName || undefined);
  const [loadingsCount, setLoadingsCount] = React.useState(0);

  const { consentTool, consentMessage } = useConsentClientTool({
    setLoadingsCount,
    createLoggerWithMeasure,
  });
  const wrapWithLoading = async <T>(fn: () => Promise<T>): Promise<T> => {
    setLoadingsCount((prev) => prev + 1);
    try {
      return await fn();
    } finally {
      setLoadingsCount((prev) => prev - 1);
    }
  };

  const sendUserMessage = useCallback(
    async (userMessageContent: string): Promise<void> => {
      return wrapWithLoading<void>(() => sendMessage(userMessageContent));
    },
    [sendMessage],
  );

  useEffect(() => {
    if (firstUserMessage && messages.length === 0 && isConversationActive()) {
      sendUserMessage(firstUserMessage);
    }
  }, [firstUserMessage, messages, sendMessage]);

  const createConversation = useCallback<CreateConversation>(
    async (createParams): Promise<string> => {
      setFirstUserMessage(
        createParams.firstUserMessage ||
          createParams.firstAssistantMessageContent,
      );
      const res = await chatClient.startChat(
        {
          type: 'new-conversation',
          origin,
        },
        domainIds,
        {
          ...consentTool,
          ...createParams.tools,
        },
        {
          noValidate: noValidate ?? false,
          enableLogging: enableLogging ?? false,
        },
      );
      if (!res.ok) {
        throw res.error;
      }
      setChat(res.chat);

      return res.chat.id;
    },
    [chatClient, domainIds, enableLogging, noValidate, setChat],
  );

  const isLoading = loadingsCount > 0;
  const messagesWithStateAction = useMemo((): ChatMessageData[] => {
    if (isLoading) {
      return [...messages, waitingMessage];
    }
    if (consentMessage) {
      return [...messages, consentMessage as ChatMessageData];
    }
    return messages;
  }, [messages.length, isLoading, consentMessage]);

  const logs = React.useMemo(
    () => requests.map((request) => request.logs).flat(),
    [requests],
  );

  return {
    isLoading,
    conversationId: conversationId ?? '',
    createConversation,
    sendUserMessage,
    statusMessage: statusMessage ?? '',
    messages: messagesWithStateAction,
    messageRequests: requests,
    logs,
  };
};
